import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, Send, Bot, ChevronDown, Copy, Check, Paperclip, X } from 'lucide-react';
import { Avatar, AvatarGroup, Tooltip } from '@heroui/react';
import { Button } from '@edusense/shared-ui-kit';
import { tokenManager } from '@edusense/shared-auth-core';
import { CHAT_TEST_IDS } from '@edusense/shared-constants';
import { motion, AnimatePresence } from 'framer-motion';
import { useFileUpload } from '../../hooks/useFileUpload';
import { MessageMedia } from '../chat/MessageMedia';
const formatBytes = (bytes)=>{
    if (!bytes || Number.isNaN(bytes)) {
        return null;
    }
    const units = [
        'B',
        'KB',
        'MB',
        'GB',
        'TB'
    ];
    let value = bytes;
    let unitIndex = 0;
    while(value >= 1024 && unitIndex < units.length - 1){
        value /= 1024;
        unitIndex += 1;
    }
    const formatted = value >= 10 ? value.toFixed(0) : value.toFixed(1);
    return `${formatted.replace(/\.0$/, '')}${units[unitIndex]}`;
};
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--theme-paper, #ffffff);
  overflow: hidden;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
  }
`;
const Header = styled.header`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper, #ffffff);
  flex-shrink: 0;
  z-index: 10;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
    border-color: var(--theme-border, #374151);
  }
`;
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;
const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;
const Title = styled.h1`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: var(--theme-text, #111827);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .dark & {
    color: var(--theme-text, #f3f4f6);
  }
`;
const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--theme-border, #e5e7eb);
  border-radius: 8px;
  background-color: transparent;
  color: var(--theme-text, #111827);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: var(--theme-hover, #f3f4f6);
  }

  .dark & {
    color: var(--theme-text, #f3f4f6);
    border-color: var(--theme-border, #374151);

    &:hover {
      background-color: var(--theme-hover, #374151);
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
const buildParticipantLabel = (participant, fallback)=>{
    if (!participant) {
        return fallback;
    }
    const rawDisplay = typeof participant.displayName === 'string' ? participant.displayName.trim() : '';
    const rawUserName = typeof participant.userName === 'string' ? participant.userName.trim() : typeof participant.name === 'string' ? participant.name.trim() : '';
    if (rawDisplay && rawUserName && rawDisplay !== rawUserName) {
        return `${rawDisplay}@${rawUserName}`;
    }
    if (rawDisplay) {
        return rawDisplay;
    }
    if (rawUserName) {
        return rawUserName;
    }
    const rawId = typeof participant.userId === 'string' ? participant.userId.trim() : typeof participant.id === 'string' ? participant.id.trim() : '';
    return rawId || fallback;
};
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #9CA3AF transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #9CA3AF;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #6B7280;
  }

  .dark &::-webkit-scrollbar-thumb {
    background: #6B7280;
  }

  .dark &::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }
`;
const MessageRow = styled('div', {
    shouldForwardProp: (prop)=>prop !== '$alignRight'
})`
  display: flex;
  flex-direction: ${({ $alignRight })=>$alignRight ? 'row-reverse' : 'row'};
  justify-content: flex-start;
  align-items: flex-end;
  gap: 0.75rem;
  width: 100%;
`;
const MessageGroup = styled(motion.div, {
    shouldForwardProp: (prop)=>prop !== '$alignRight'
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  max-width: 75%;
  align-items: ${({ $alignRight })=>$alignRight ? 'flex-end' : 'flex-start'};

  &:hover [data-role="message-actions"] {
    opacity: 1;
  }

  @media (max-width: 640px) {
    max-width: 85%;
  }
`;
const MessageContent = styled('div', {
    shouldForwardProp: (prop)=>prop !== '$alignRight'
})`
  display: flex;
  flex-direction: column;
  align-items: ${({ $alignRight })=>$alignRight ? 'flex-end' : 'flex-start'};
  max-width: 100%;
`;
const MessageBubble = styled('div', {
    shouldForwardProp: (prop)=>prop !== '$isCurrentUser'
})`
  display: inline-flex;
  flex-direction: column;
  width: fit-content;
  max-width: 100%;
  padding: 0.75rem 1rem;
  border-radius: ${(props)=>props.$isCurrentUser ? '1.25rem 1.25rem 0.25rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0.25rem'};
  align-self: ${(props)=>props.$isCurrentUser ? 'flex-end' : 'flex-start'};
  background-color: ${(props)=>props.$isCurrentUser ? 'var(--theme-primary, #3b82f6)' : 'var(--theme-paper-active, #f3f4f6)'};
  color: ${(props)=>props.$isCurrentUser ? 'white' : 'var(--theme-text, #111827)'};
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  .dark & {
    background-color: ${(props)=>props.$isCurrentUser ? 'var(--theme-primary, #2563eb)' : 'var(--theme-paper-active, #374151)'};
    color: ${(props)=>props.$isCurrentUser ? 'white' : 'var(--theme-text, #f3f4f6)'};
  }

  @media (max-width: 640px) {
    max-width: 95%;
  }
`;
const MessageAuthor = styled('span', {
    shouldForwardProp: (prop)=>prop !== '$isCurrentUser'
})`
  font-size: 0.75rem;
  color: var(--theme-text-secondary, #6b7280);
  margin-bottom: 0.35rem;
  align-self: ${({ $isCurrentUser })=>$isCurrentUser ? 'flex-end' : 'flex-start'};

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const MessageActions = styled(motion.div, {
    shouldForwardProp: (prop)=>prop !== '$isCurrentUser'
})`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
  align-self: ${(props)=>props.$isCurrentUser ? 'flex-end' : 'flex-start'};
`;
const MessageAvatar = styled(Avatar)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;
const InputContainer = styled.div`
  padding: 1rem;
  border-top: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper, #ffffff);
  flex-shrink: 0;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
    border-color: var(--theme-border, #374151);
  }
`;
const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--theme-input, #f9fafb);
  border-radius: 24px;
  padding: 8px 12px 8px 16px;
  border: 1px solid var(--theme-border, #e5e7eb);
  gap: 8px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--theme-primary, #3b82f6);
  }

  .dark & {
    background-color: var(--theme-input, #374151);
    border-color: var(--theme-border, #4b5563);
  }
`;
const AttachmentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--theme-text-secondary, #6b7280);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--theme-hover, #e5e7eb);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);

    &:hover {
      background-color: #4b5563;
    }
  }
`;
const Input = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  color: var(--theme-text, #111827);
  outline: none;

  &::placeholder {
    color: var(--theme-text-secondary, #6b7280);
  }

  .dark & {
    color: var(--theme-text, #f3f4f6);

    &::placeholder {
      color: var(--theme-text-secondary, #9ca3af);
    }
  }
`;
const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: ${(props)=>props.$enabled ? 'var(--theme-primary, #3b82f6)' : '#d1d5db'};
  color: white;
  border: none;
  border-radius: 50%;
  cursor: ${(props)=>props.$enabled ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    transform: ${(props)=>props.$enabled ? 'scale(1.05)' : 'none'};
  }

  &:active {
    transform: ${(props)=>props.$enabled ? 'scale(0.95)' : 'none'};
  }
`;
const AttachmentSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0;
`;
const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background-color: var(--theme-input, #f3f4f6);
  border: 1px solid var(--theme-border, #e5e7eb);
  font-size: 0.85rem;

  .dark & {
    background-color: #374151;
    border-color: #4b5563;
  }
`;
const AttachmentRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--theme-text-secondary, #6b7280);
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;
const UploadProgress = styled.div`
  width: 120px;
  height: 6px;
  border-radius: 999px;
  background-color: rgba(59, 130, 246, 0.15);
  overflow: hidden;
`;
const UploadProgressFill = styled.div`
  width: ${(props)=>Math.max(4, props.$progress)}%;
  height: 100%;
  transition: width 0.2s ease;
  background: linear-gradient(135deg, var(--theme-primary, #3b82f6), #2563eb);
`;
const UploadStatusText = styled.span`
  font-size: 0.75rem;
  color: var(--theme-text-secondary, #6b7280);

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const UploadErrorText = styled.span`
  font-size: 0.75rem;
  color: #dc2626;
`;
const AttachmentPreview = styled.div`
  width: 100%;
  max-width: 20rem;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.18);
  border: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper, #ffffff);

  .dark & {
    border-color: #374151;
    background-color: #1f2937;
  }

  video,
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;
const MessageMediaWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;
const AttachmentMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: var(--theme-text-secondary, #6b7280);

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const AttachmentDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.6rem;
  border-radius: 0.4rem;
  border: 1px solid var(--theme-border, #e5e7eb);
  color: var(--theme-primary, #2563eb);
  font-weight: 500;
  text-decoration: none;

  &:hover {
    background-color: rgba(37, 99, 235, 0.08);
  }

  .dark & {
    border-color: var(--theme-border, #374151);
    color: var(--theme-primary, #60a5fa);

    &:hover {
      background-color: rgba(96, 165, 250, 0.12);
    }
  }
`;
const ScrollToBottomButton = styled(motion.button)`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--theme-primary, #3b82f6) 0%, #2563eb 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;
const ChatConversationView = ({ chatRoom, onBack, onSwitchToAIChat, onSendMessage, currentUserId, rsocketClient, ensureRSocketConnection, isRSocketConnecting = false })=>{
    const [newMessage, setNewMessage] = useState('');
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [attachment, setAttachment] = useState(null);
    const [attachmentName, setAttachmentName] = useState(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
    const [attachmentSize, setAttachmentSize] = useState(null);
    const attachmentSizeLabel = useMemo(()=>{
        const size = attachment?.size ?? attachmentSize;
        return formatBytes(size);
    }, [
        attachment?.size,
        attachmentSize
    ]);
    const messages = Array.isArray(chatRoom.messages) ? chatRoom.messages : [];
    const participantLookup = useMemo(()=>{
        const participants = Array.isArray(chatRoom.participants) ? chatRoom.participants : [];
        const lookup = new Map();
        participants.forEach((participant)=>{
            const candidateId = participant?.userId ?? participant?.id ?? participant?.user?.id;
            if (candidateId != null) {
                lookup.set(String(candidateId), participant);
            }
        });
        return lookup;
    }, [
        chatRoom.participants
    ]);
    const resolvedCurrentUserId = currentUserId ? String(currentUserId) : null;
    const rsocketRequester = useMemo(()=>({
            requestResponse: async (route, data)=>{
                const client = rsocketClient ?? await ensureRSocketConnection();
                if (!client) {
                    throw new Error('파일 업로드를 위한 연결이 준비되지 않았습니다.');
                }
                return client.requestResponse(route, data);
            }
        }), [
        ensureRSocketConnection,
        rsocketClient
    ]);
    const { isUploading, progress, error: uploadError, uploadFile, reset: resetUploadState } = useFileUpload({
        rsocket: rsocketRequester,
        getAccessToken: ()=>tokenManager.getToken() ?? null
    });
    const showAttachmentSummary = Boolean(attachmentName || uploadError);
    const getOtherParticipants = (chatRoom)=>{
        return chatRoom.participants?.filter((p)=>p.id !== 'current_user') || [];
    };
    const getChatTitle = (chatRoom)=>{
        const others = getOtherParticipants(chatRoom);
        if (!others.length) {
            return '알 수 없는 사용자';
        }
        if (chatRoom.isGroupThread) {
            return others.map((participant, index)=>buildParticipantLabel(participant, `참여자 ${index + 1}`)).join(', ');
        }
        return buildParticipantLabel(others[0], '알 수 없는 사용자');
    };
    const handleAttachmentButtonClick = useCallback(()=>{
        if (!rsocketClient) {
            ensureRSocketConnection().catch((error)=>{
                console.warn('[ChatConversationView] Failed to prepare RSocket for attachment:', error);
            });
        }
        fileInputRef.current?.click();
    }, [
        ensureRSocketConnection,
        rsocketClient
    ]);
    const handleFileChange = useCallback(async (event)=>{
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setAttachmentName(file.name);
        setAttachmentSize(file.size);
        const previewUrl = URL.createObjectURL(file);
        setLocalPreviewUrl((prev)=>{
            if (prev) {
                URL.revokeObjectURL(prev);
            }
            return previewUrl;
        });
        setAttachment({
            mediaUrl: previewUrl,
            fileId: `local-${Date.now()}`,
            fileName: file.name,
            contentType: file.type,
            size: file.size
        });
        try {
            const result = await uploadFile(file);
            setLocalPreviewUrl((prev)=>{
                if (prev) {
                    URL.revokeObjectURL(prev);
                }
                return null;
            });
            setAttachment({
                mediaUrl: result.mediaUrl,
                fileId: result.fileId,
                fileName: file.name,
                contentType: file.type,
                size: file.size
            });
        } catch (error) {
            console.error('파일 업로드에 실패했습니다:', error);
            const errorName = error instanceof Error ? error.name : undefined;
            if (errorName === 'UploadValidationError' || errorName === 'UploadUrlUnavailable') {
                setLocalPreviewUrl((prev)=>{
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return null;
                });
                setAttachment(null);
                setAttachmentSize(file.size);
                setAttachmentName(file.name);
            }
        } finally{
            event.target.value = '';
        }
    }, [
        uploadFile
    ]);
    const handleRemoveAttachment = ()=>{
        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
            setLocalPreviewUrl(null);
        }
        setAttachment(null);
        setAttachmentName(null);
        setAttachmentSize(null);
        resetUploadState();
    };
    const isSendDisabled = !newMessage.trim() && !attachment || isUploading;
    const isAttachmentDisabled = isUploading;
    const isMessageInputDisabled = Boolean(isRSocketConnecting && !rsocketClient);
    const handleSendMessage = ()=>{
        const trimmed = newMessage.trim();
        if (!trimmed && !attachment) {
            return;
        }
        const fallbackContent = attachment ? attachment.fileName || '파일을 전송했습니다.' : '';
        onSendMessage({
            content: trimmed || fallbackContent || '파일을 전송했습니다.',
            media: attachment ?? undefined
        });
        setNewMessage('');
        if (attachment) {
            if (localPreviewUrl) {
                URL.revokeObjectURL(localPreviewUrl);
                setLocalPreviewUrl(null);
            }
            setAttachment(null);
            setAttachmentName(null);
            setAttachmentSize(null);
            resetUploadState();
        }
    };
    const handleCopyMessage = async (content, messageId)=>{
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageId(messageId);
            setTimeout(()=>setCopiedMessageId(null), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };
    const scrollToBottom = ()=>{
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    };
    const handleScroll = ()=>{
        if (!scrollContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isNearBottom);
    };
    useEffect(()=>{
        scrollToBottom();
    }, [
        chatRoom.messages
    ]);
    const otherParticipants = getOtherParticipants(chatRoom);
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsx(Header, {
                children: /*#__PURE__*/ _jsxs(TitleRow, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            iconOnly: true,
                            icon: /*#__PURE__*/ _jsx(ArrowLeft, {
                                size: 18
                            }),
                            variant: "ghost",
                            size: "sm",
                            onClick: onBack,
                            label: "뒤로가기",
                            "data-testid": CHAT_TEST_IDS.BACK_TO_LIST_BUTTON
                        }),
                        /*#__PURE__*/ _jsxs(TitleGroup, {
                            children: [
                                chatRoom.isGroupThread ? /*#__PURE__*/ _jsx(AvatarGroup, {
                                    size: "sm",
                                    max: 3,
                                    children: otherParticipants.slice(0, 3).map((p)=>/*#__PURE__*/ _jsx(Avatar, {
                                            src: p.avatarUrl,
                                            alt: buildParticipantLabel(p, p.userId || 'participant'),
                                            size: "sm"
                                        }, p.id || p.userId))
                                }) : otherParticipants[0] && /*#__PURE__*/ _jsx(Avatar, {
                                    src: otherParticipants[0]?.avatarUrl,
                                    alt: buildParticipantLabel(otherParticipants[0], otherParticipants[0]?.userId || 'participant'),
                                    size: "sm"
                                }),
                                /*#__PURE__*/ _jsx(Title, {
                                    children: getChatTitle(chatRoom)
                                })
                            ]
                        }),
                        onSwitchToAIChat && /*#__PURE__*/ _jsxs(SwitchButton, {
                            onClick: onSwitchToAIChat,
                            children: [
                                /*#__PURE__*/ _jsx(Bot, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "AI 튜터"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column'
                },
                children: [
                    /*#__PURE__*/ _jsxs(MessagesContainer, {
                        ref: scrollContainerRef,
                        onScroll: handleScroll,
                        children: [
                            messages.map((msg, index)=>{
                                const messageUserId = String(msg?.user?.id ?? msg?.userId ?? msg?.sender?.id ?? '');
                                const isCurrentUser = resolvedCurrentUserId ? messageUserId === resolvedCurrentUserId : messageUserId === 'current_user';
                                const isNew = index === messages.length - 1;
                                const uniqueIdentifier = msg?.id || msg?.messageId || `${msg?.roomId ?? 'message'}-${index}`;
                                const participant = participantLookup.get(messageUserId);
                                const authorName = msg?.user?.name ?? msg?.user?.userName ?? msg?.user?.displayName ?? participant?.displayName ?? participant?.name ?? participant?.userName ?? (messageUserId && messageUserId !== 'unknown-user' ? messageUserId : '알 수 없는 사용자');
                                const displayAuthor = isCurrentUser ? '나' : authorName;
                                const avatarSrc = msg?.user?.avatarImageLink ?? participant?.avatarUrl ?? null;
                                const messageAttachment = msg?.attachment ?? null;
                                const attachmentFileName = messageAttachment?.fileName ?? msg?.fileName ?? msg?.mediaFileName ?? msg?.mediaUrl?.split('/')?.pop?.()?.split('?')[0] ?? '첨부파일';
                                const attachmentFileSizeLabel = formatBytes(messageAttachment?.size);
                                const hasDownloadUrl = Boolean(msg?.mediaUrl);
                                return /*#__PURE__*/ _jsxs(MessageRow, {
                                    $alignRight: isCurrentUser,
                                    children: [
                                        !isCurrentUser && /*#__PURE__*/ _jsx(MessageAvatar, {
                                            name: authorName,
                                            src: avatarSrc ?? undefined
                                        }),
                                        /*#__PURE__*/ _jsxs(MessageGroup, {
                                            "data-testid": `${CHAT_TEST_IDS.MESSAGE_PREFIX}-${index}`,
                                            $alignRight: isCurrentUser,
                                            initial: isNew ? {
                                                opacity: 0,
                                                y: 12
                                            } : false,
                                            animate: {
                                                opacity: 1,
                                                y: 0
                                            },
                                            transition: {
                                                duration: 0.25,
                                                ease: 'easeOut'
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsxs(MessageContent, {
                                                    $alignRight: isCurrentUser,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(MessageAuthor, {
                                                            $isCurrentUser: isCurrentUser,
                                                            children: displayAuthor
                                                        }),
                                                        /*#__PURE__*/ _jsxs(MessageBubble, {
                                                            $isCurrentUser: isCurrentUser,
                                                            "data-status": msg?.status ?? undefined,
                                                            children: [
                                                                (msg.text || msg.content) && /*#__PURE__*/ _jsx("span", {
                                                                    children: msg.text || msg.content || ''
                                                                }),
                                                                msg?.mediaUrl && /*#__PURE__*/ _jsxs(MessageMediaWrapper, {
                                                                    "data-testid": `${CHAT_TEST_IDS.MESSAGE_PREFIX}-${index}${CHAT_TEST_IDS.MESSAGE_FILE_SUFFIX}`,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(MessageMedia, {
                                                                            mediaUrl: msg?.mediaUrl ?? undefined,
                                                                            contentType: msg?.contentType ?? undefined
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs(AttachmentMeta, {
                                                                            children: [
                                                                                /*#__PURE__*/ _jsxs("span", {
                                                                                    "data-testid": `${CHAT_TEST_IDS.MESSAGE_PREFIX}-${index}${CHAT_TEST_IDS.MESSAGE_FILE_NAME_SUFFIX}`,
                                                                                    children: [
                                                                                        attachmentFileName,
                                                                                        attachmentFileSizeLabel ? ` · ${attachmentFileSizeLabel}` : ''
                                                                                    ]
                                                                                }),
                                                                                hasDownloadUrl ? /*#__PURE__*/ _jsx(AttachmentDownloadLink, {
                                                                                    href: msg?.mediaUrl ?? undefined,
                                                                                    download: attachmentFileName,
                                                                                    target: "_blank",
                                                                                    rel: "noreferrer",
                                                                                    "data-testid": `${CHAT_TEST_IDS.MESSAGE_PREFIX}-${index}${CHAT_TEST_IDS.MESSAGE_DOWNLOAD_SUFFIX}`,
                                                                                    children: "다운로드"
                                                                                }) : null
                                                                            ]
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(MessageActions, {
                                                    "data-role": "message-actions",
                                                    $isCurrentUser: isCurrentUser,
                                                    initial: {
                                                        opacity: 0
                                                    },
                                                    animate: {
                                                        opacity: 1
                                                    },
                                                    children: /*#__PURE__*/ _jsx(Tooltip, {
                                                        content: copiedMessageId === msg.id ? '복사됨' : '메시지 복사',
                                                        placement: "top",
                                                        children: /*#__PURE__*/ _jsx("button", {
                                                            onClick: ()=>handleCopyMessage(msg.text || msg.content || '', msg.id),
                                                            style: {
                                                                background: 'transparent',
                                                                border: 'none',
                                                                padding: '4px',
                                                                cursor: 'pointer',
                                                                borderRadius: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: 'var(--theme-text-secondary, #6b7280)'
                                                            },
                                                            children: /*#__PURE__*/ _jsx(AnimatePresence, {
                                                                mode: "wait",
                                                                children: copiedMessageId === msg.id ? /*#__PURE__*/ _jsx(motion.div, {
                                                                    initial: {
                                                                        scale: 0
                                                                    },
                                                                    animate: {
                                                                        scale: 1
                                                                    },
                                                                    exit: {
                                                                        scale: 0
                                                                    },
                                                                    children: /*#__PURE__*/ _jsx(Check, {
                                                                        size: 14,
                                                                        color: "var(--theme-success, #10b981)"
                                                                    })
                                                                }, "check") : /*#__PURE__*/ _jsx(motion.div, {
                                                                    initial: {
                                                                        scale: 0
                                                                    },
                                                                    animate: {
                                                                        scale: 1
                                                                    },
                                                                    exit: {
                                                                        scale: 0
                                                                    },
                                                                    children: /*#__PURE__*/ _jsx(Copy, {
                                                                        size: 14
                                                                    })
                                                                }, "copy")
                                                            })
                                                        })
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                }, uniqueIdentifier);
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                ref: messagesEndRef
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(AnimatePresence, {
                        children: showScrollButton && /*#__PURE__*/ _jsx(ScrollToBottomButton, {
                            onClick: scrollToBottom,
                            initial: {
                                scale: 0,
                                opacity: 0
                            },
                            animate: {
                                scale: 1,
                                opacity: 1
                            },
                            exit: {
                                scale: 0,
                                opacity: 0
                            },
                            transition: {
                                duration: 0.2
                            },
                            children: /*#__PURE__*/ _jsx(ChevronDown, {
                                size: 20
                            })
                        })
                    })
                ]
            }),
            showAttachmentSummary && /*#__PURE__*/ _jsxs(AttachmentSummary, {
                "data-testid": CHAT_TEST_IDS.FILE_PREVIEW,
                children: [
                    /*#__PURE__*/ _jsxs(AttachmentChip, {
                        children: [
                            /*#__PURE__*/ _jsx(Paperclip, {
                                size: 14
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    attachmentName ?? '파일 첨부 오류',
                                    attachmentSizeLabel ? ` · ${attachmentSizeLabel}` : ''
                                ]
                            }),
                            isUploading ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(UploadProgress, {
                                        children: /*#__PURE__*/ _jsx(UploadProgressFill, {
                                            $progress: Math.min(100, Math.max(0, progress))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(UploadStatusText, {
                                        children: [
                                            Math.min(100, Math.max(0, progress)).toFixed(0),
                                            "%"
                                        ]
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(AttachmentRemoveButton, {
                                onClick: handleRemoveAttachment,
                                "aria-label": "첨부 파일 제거",
                                children: /*#__PURE__*/ _jsx(X, {
                                    size: 12
                                })
                            })
                        ]
                    }),
                    attachment && attachmentSizeLabel && /*#__PURE__*/ _jsx(UploadStatusText, {
                        "data-testid": CHAT_TEST_IDS.FILE_SIZE,
                        children: attachmentSizeLabel
                    }),
                    attachment?.mediaUrl && /*#__PURE__*/ _jsx(AttachmentPreview, {
                        children: /*#__PURE__*/ _jsx(MessageMedia, {
                            mediaUrl: attachment.mediaUrl,
                            contentType: attachment.contentType
                        })
                    }),
                    uploadError && /*#__PURE__*/ _jsx(UploadErrorText, {
                        "data-testid": CHAT_TEST_IDS.FILE_ERROR,
                        children: uploadError
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(InputContainer, {
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        ref: fileInputRef,
                        type: "file",
                        style: {
                            display: 'none'
                        },
                        onChange: handleFileChange,
                        "data-testid": CHAT_TEST_IDS.FILE_UPLOAD_INPUT
                    }),
                    /*#__PURE__*/ _jsxs(InputWrapper, {
                        children: [
                            /*#__PURE__*/ _jsx(AttachmentButton, {
                                type: "button",
                                onClick: handleAttachmentButtonClick,
                                disabled: isAttachmentDisabled,
                                "aria-label": "파일 첨부",
                                "data-testid": CHAT_TEST_IDS.FILE_UPLOAD_BUTTON,
                                children: /*#__PURE__*/ _jsx(Paperclip, {
                                    size: 16
                                })
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                "data-testid": CHAT_TEST_IDS.MESSAGE_INPUT,
                                placeholder: isUploading ? '파일 업로드 중...' : '메시지를 입력하세요...',
                                value: newMessage,
                                disabled: isMessageInputDisabled,
                                onChange: (e)=>setNewMessage(e.target.value),
                                onKeyPress: (e)=>{
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (!isSendDisabled) {
                                            handleSendMessage();
                                        }
                                    }
                                }
                            }),
                            /*#__PURE__*/ _jsx(SendButton, {
                                $enabled: !isSendDisabled,
                                onClick: handleSendMessage,
                                disabled: isSendDisabled,
                                "data-testid": CHAT_TEST_IDS.SEND_BUTTON,
                                "aria-label": "메시지 전송",
                                children: /*#__PURE__*/ _jsx(Send, {
                                    size: 16
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default ChatConversationView;
